import { setCoverageReportConfig, mergeCoverageReportConfig } from '@didi/prisma'
import store from '@/store'
import { isBApp, isBDApp, inMerchantManager } from '@/utils/common/env'
import { isFromBDAPP } from '@/utils/bridge/fromBDAPP'
import { getUserInfo } from '@/utils/bridge/bridge'
import { getQueryObj } from '@/utils/common/query'

/** MQP serviceId（https://mqp.intra.xiaojukeji.com/fe/pipeline/list） */
const COVERAGE_SERVICE_ID = 93626

/** endType：1-B App；2-BD App；3-管理端 App；4-browser（MQP 枚举） */
export const CoverageEndType = {
  B_APP: '1',
  BD_APP: '2',
  MANAGER_APP: '3',
  BROWSER: '4',
}

const pickFirstNonEmpty = (...values) => {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      return String(value).trim()
    }
  }
  return ''
}

/** 与 request.js 拦截器 getCountryCode 逻辑一致，仅供覆盖率上报 */
const getCountryCode = () => {
  const state = store.state

  if (state.currentShop?.countryCode) {
    return state.currentShop.countryCode
  }

  if (state.currentContractor?.country) {
    return state.currentContractor.country
  }

  if (state.country) {
    return state.country
  }

  try {
    const pathname = window.location.pathname
    const pathSegments = pathname.split('/').filter(segment => segment)
    if (pathSegments.length > 0) {
      const firstSegment = pathSegments[0]
      if (firstSegment.includes('-')) {
        const countryCode = firstSegment.split('-')[1]
        if (countryCode) {
          return countryCode.toUpperCase()
        }
      }
    }
  } catch (error) {
    console.warn('从URL获取国家码失败:', error)
  }

  return null
}

const shouldReadCoverageFromQuery = () => isBDApp || isFromBDAPP()

const getBdAppCoverageContextFromQuery = () => {
  const { username = '', countryCode = '' } = getQueryObj()
  return {
    userName: pickFirstNonEmpty(username),
    countryCode: pickFirstNonEmpty(countryCode),
  }
}

/** 管理端 App：countryCode 与 request 拦截器 getCountryCode 保持一致 */
const getManagerAppCoverageContext = () => {
  const { globalParams = {}, currentContractor = {} } = store.state || {}
  return {
    userName: pickFirstNonEmpty(globalParams.uid, currentContractor.uid),
    countryCode: pickFirstNonEmpty(getCountryCode()),
  }
}

const getCoverageUserContext = () => {
  if (inMerchantManager) {
    return getManagerAppCoverageContext()
  }

  if (shouldReadCoverageFromQuery()) {
    return getBdAppCoverageContextFromQuery()
  }

  const { globalParams = {}, currentShop = {}, country = '' } = store.state || {}
  return {
    userName: pickFirstNonEmpty(globalParams.uid, globalParams.username),
    countryCode: pickFirstNonEmpty(globalParams.countryCode, currentShop.countryCode, country),
  }
}

const getEndType = () => {
  // 管理端 UA 含 soda.manager，须先于 isBDApp 判断
  if (inMerchantManager) {
    return CoverageEndType.MANAGER_APP
  }
  if (isBApp) {
    return CoverageEndType.B_APP
  }
  // isFromBDAPP 仅表示从 BD 跳转，不算 BD App 容器；endType 只看 UA
  if (isBDApp) {
    return CoverageEndType.BD_APP
  }
  return CoverageEndType.BROWSER
}

export const initCoverageConfig = () => {
  setCoverageReportConfig({
    serviceId: COVERAGE_SERVICE_ID,
    endType: getEndType(),
  })
}

export const mergeCoverageEnvConfig = (override = {}) => {
  const context = getCoverageUserContext()
  mergeCoverageReportConfig({
    userName: pickFirstNonEmpty(override.userName, context.userName),
    countryCode: pickFirstNonEmpty(override.countryCode, context.countryCode),
  })
}

/** 管理端：store 可能无 uid，通过 merchant.getUserInfo 异步补全 */
export const mergeManagerCoverageEnvConfig = (override = {}) => {
  mergeCoverageEnvConfig(override)

  if (!inMerchantManager) {
    return
  }

  const context = getManagerAppCoverageContext()
  if (context.userName && context.countryCode) {
    return
  }

  getUserInfo()
    .then(userInfo => {
      mergeCoverageEnvConfig({
        userName: pickFirstNonEmpty(override.userName, context.userName, userInfo?.uid),
        countryCode: pickFirstNonEmpty(override.countryCode, getCountryCode()),
      })
    })
    .catch(error => {
      console.warn('[coverage] mergeManagerCoverageEnvConfig getUserInfo failed:', error)
    })
}
